import type { StorefrontNetworkAndPvRecCategory } from '@api/storefrontsNetworkPvRec';
import type { Raw } from '@typings/vendor';

/**
 * Transforms and deduplicates vendors from storefront network and PVRec sources.
 * 1. Excludes vendors that already have an active conversation.
 * 2. Prioritizes 'storefrontNetwork' vendors and attaches a contextual badge.
 * 3. Adds 'pvRec' vendors only if they aren't already present (deduplication).
 * 4. Limits the result to 4 items on mobile devices.
 */
export const getUniqueRecommendedVendors = (
	category: StorefrontNetworkAndPvRecCategory | undefined,
	contactedIds: Set<string>,
	isMobile: boolean,
): Raw[] => {
	if (!category) return [];

	const vendorMap = new Map<string, Raw>();

	// 1. Process Storefront Network (High Priority + Venue Badge)
	const storefrontNetwork = category.storefrontNetwork ?? [];
	for (const sf of storefrontNetwork) {
		const vendorId = sf.profile?.id;
		if (vendorId && !contactedIds.has(vendorId)) {
			vendorMap.set(vendorId, {
				...sf.profile,
				badge: sf.nInteractions
					? `${sf.nInteractions} ${sf.nInteractions === 1 ? 'wedding' : 'weddings'} at your venue`
					: undefined,
			});
		}
	}

	// 2. Process PVRec (Lower Priority - Only add if ID is unique)
	const pvRec = category.pvRec ?? [];
	for (const vendor of pvRec) {
		const vendorId = vendor.id;
		if (vendorId && !contactedIds.has(vendorId) && !vendorMap.has(vendorId)) {
			vendorMap.set(vendorId, vendor);
		}
	}

	const uniqueVendors = Array.from(vendorMap.values());

	return isMobile ? uniqueVendors.slice(0, 4) : uniqueVendors;
};
